import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { getFirebaseDb } from '../services/firebase';
import { computeEffectiveConfidence } from '../utils/beliefDecay';
import type { BeliefNode, BeliefEdge, CognitiveMotif, GraphStatistics, NodeDomain, BeliefTier } from '../types';

interface UseBeliefGraphReturn {
  nodes: BeliefNode[];
  edges: BeliefEdge[];
  motifs: CognitiveMotif[];
  stats: GraphStatistics | null;
  loading: boolean;
  nodesByDomain: Partial<Record<NodeDomain, BeliefNode[]>>;
  nodesByTier: Record<BeliefTier, BeliefNode[]>;
  archivedNodes: BeliefNode[];
}

export function useBeliefGraph(userId: string | null): UseBeliefGraphReturn {
  const [rawNodes, setRawNodes] = useState<BeliefNode[]>([]);
  const [edges, setEdges] = useState<BeliefEdge[]>([]);
  const [motifs, setMotifs] = useState<CognitiveMotif[]>([]);
  const [stats] = useState<GraphStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const db = getFirebaseDb();
    let resolvedCount = 0;
    const totalSubscriptions = 3;

    const checkDone = () => {
      resolvedCount++;
      if (resolvedCount >= totalSubscriptions) setLoading(false);
    };

    const unsubNodes = onSnapshot(
      collection(db, 'users', userId, 'nodes'),
      snap => {
        setRawNodes(snap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefNode)));
        checkDone();
      },
      () => checkDone()
    );

    const unsubEdges = onSnapshot(
      collection(db, 'users', userId, 'edges'),
      snap => {
        setEdges(snap.docs.map(d => ({ id: d.id, ...d.data() } as BeliefEdge)));
        checkDone();
      },
      () => checkDone()
    );

    const unsubMotifs = onSnapshot(
      collection(db, 'users', userId, 'motifs'),
      snap => {
        setMotifs(snap.docs.map(d => ({ id: d.id, ...d.data() } as CognitiveMotif)));
        checkDone();
      },
      () => checkDone()
    );

    return () => {
      unsubNodes();
      unsubEdges();
      unsubMotifs();
    };
  }, [userId]);

  const { nodes, archivedNodes, nodesByDomain, nodesByTier } = useMemo(() => {
    const now = new Date();
    const active: BeliefNode[] = [];
    const archived: BeliefNode[] = [];

    for (const node of rawNodes) {
      if ((node.status || 'active') === 'archived' || node.isConsolidated) {
        archived.push(node);
        continue;
      }
      const effectiveConfidence = computeEffectiveConfidence(node, now);
      active.push({ ...node, confidence: effectiveConfidence });
    }

    const byDomain: Partial<Record<NodeDomain, BeliefNode[]>> = {};
    const byTier: Record<BeliefTier, BeliefNode[]> = { axiomatic: [], active: [], situational: [] };

    for (const node of active) {
      const tier = node.tier || 'active';
      byTier[tier].push(node);
      for (const domain of node.domain) {
        if (!byDomain[domain]) byDomain[domain] = [];
        byDomain[domain]!.push(node);
      }
    }

    return { nodes: active, archivedNodes: archived, nodesByDomain: byDomain, nodesByTier: byTier };
  }, [rawNodes]);

  return { nodes, edges, motifs, stats, loading, nodesByDomain, nodesByTier, archivedNodes };
}
